import React, {
  useEffect, useState, useMemo, useRef, useCallback,
} from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  Map as MapIcon, Loader2, Building2, AlertTriangle,
  X, Eye, CalendarPlus, ClipboardList, SlidersHorizontal,
  Pencil, Search, MapPin,
} from 'lucide-react';
import {
  MapContainer, TileLayer, GeoJSON, ZoomControl,
  useMap, useMapEvents,
} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { useNavigate } from 'react-router-dom';
import { req } from '../services/api';
import { CCAA_ES, REGIONES_PT } from '../constants/geografia';
import MapaStatsBar from '../components/mapa/MapaStatsBar';
import MapaEntityMarker, { ENTITY_LEGEND } from '../components/mapa/MapaEntityMarker';
import MapaEntityPanel from '../components/mapa/MapaEntityPanel';
import MapaMobileBottomSheet from '../components/mapa/MapaMobileBottomSheet';
import MapaLocateButton from '../components/mapa/MapaLocateButton';
import MapaDrawerFiltros from '../components/mapa/MapaDrawerFiltros';

const GEO_URL = '/geo/municipios_es.geojson';
const NOMINATIM = 'https://nominatim.openstreetmap.org/search';

// ── Colores por grupo empresarial (modo MERCADO) ──────────────────────────────

const GRUPO_COLORS = {
  veolia:                '#1E40AF',
  aqualia:               '#EA580C',
  acciona:               '#15803D',
  global_omnium:         '#7C3AED',
  facsa:                 '#0891B2',
  saur:                  '#BE185D',
  sacyr:                 '#B45309',
  municipal:             '#4B5563',
  independiente_publico: '#0F766E',
  independiente_privado: '#92400E',
  canal_isabel_ii:       '#0284C7',
  aqlara:                '#DC2626',
  sumem:                 '#9333EA',
  aigues_terrassa:       '#0891B2',
  // Operadores públicos andaluces (color propio; se distinguen por textura)
  emasesa:               '#DC2626',
  giahsa:                '#7C3AED',
  aljarafesa:            '#059669',
  acosol:                '#0369A1',
  // Operadores supramunicipales con color propio
  catsa:                 '#0D9488',
  atll:                  '#0369A1',
  mcp_pamplona:          '#6D28D9',
};

const GRUPO_LABELS = {
  veolia:                'Veolia',
  aqualia:               'Aqualia',
  acciona:               'Acciona',
  global_omnium:         'Global Omnium',
  facsa:                 'FACSA',
  saur:                  'SAUR',
  sacyr:                 'Sacyr',
  municipal:             'Municipal',
  independiente_publico: 'Ind. público',
  independiente_privado: 'Ind. privado',
  canal_isabel_ii:       'Canal Isabel II',
  aqlara:                'Aqlara',
  sumem:                 'SUMEM',
  aigues_terrassa:       'Aigües Terrassa',
  emasesa:               'EMASESA',
  giahsa:                'GIAHSA',
  aljarafesa:            'ALJARAFESA',
  acosol:                'ACOSOL',
  catsa:                 'CATSA',
  atll:                  'ATLL',
  mcp_pamplona:          'MCP Pamplona',
};

const TIPO_GESTION_LABELS = {
  empresa_publica: 'Empresa pública',
  empresa_mixta:   'Empresa mixta',
  concesion:       'Concesión privada',
  directa_municipal: 'Gestión directa',
  mancomunidad:    'Mancomunidad',
  consorcio:       'Consorcio',
  organismo_autonomo: 'Org. autónomo',
};

function grupoColor(grupo) {
  return GRUPO_COLORS[grupo] ?? '#E5E7EB';
}

// ── Naturaleza jurídica del operador (para texturas del mapa) ─────────────────
// Determina el patrón (textura) de relleno del polígono según el grupo.

const GRUPOS_PUBLICOS = [
  'municipal', 'canal_isabel_ii', 'independiente_publico', 'emasesa', 'giahsa',
  'acosol', 'aljarafesa', 'catsa', 'atll', 'mcp_pamplona', 'sumem', 'aigues_terrassa',
];
const GRUPOS_PRIVADOS = [
  'veolia', 'aqualia', 'acciona', 'global_omnium', 'facsa', 'saur', 'sacyr',
  'aqlara', 'independiente_privado',
];
const GRUPOS_MUNICIPALES = ['municipal'];
const GRUPOS_MIXTOS = []; // por ahora vacío

function naturalezaGrupo(grupo) {
  if (GRUPOS_MUNICIPALES.includes(grupo)) return 'municipal';
  if (GRUPOS_PUBLICOS.includes(grupo)) return 'publico';
  if (GRUPOS_PRIVADOS.includes(grupo)) return 'privado';
  return 'privado'; // fallback
}

// Todos los grupos que necesitan textura (no-privados). Unión de GRUPO_COLORS y
// las listas de naturaleza, porque algunos supramunicipales (catsa, atll,
// mcp_pamplona) no tienen color propio pero sí pueden aparecer como grupo y
// styleFeature referenciará su url(#pat-…).
const GRUPOS_CON_TEXTURA = Array.from(new Set([
  ...Object.keys(GRUPO_COLORS),
  ...GRUPOS_PUBLICOS, ...GRUPOS_MUNICIPALES, ...GRUPOS_MIXTOS,
])).filter((g) => naturalezaGrupo(g) !== 'privado');

// ── Provincia/CCAA client-side ────────────────────────────────────────────────
// El endpoint /mapa no devuelve `ccaa`; se deriva de `provincia`. m.provincia
// puede venir bilingüe ("Alicante/Alacant"), así que se casa por segmentos
// contra los nombres de CCAA_ES (provincia canónica + su comunidad).
const PROV_TO_CCAA = {};
for (const [comunidad, provincias] of Object.entries(CCAA_ES)) {
  for (const p of provincias) PROV_TO_CCAA[p] = comunidad;
}
function provOptDe(m) {
  const segs = String(m?.provincia || '').split('/').map((s) => s.trim());
  return segs.find((s) => PROV_TO_CCAA[s]) || segs[0] || null;
}

// ── FitBounds ────────────────────────────────────────────────────────────────

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    const lats = points.map((p) => p.lat);
    const lngs = points.map((p) => p.lng);
    map.fitBounds(
      [[Math.min(...lats), Math.min(...lngs)], [Math.max(...lats), Math.max(...lngs)]],
      { padding: [40, 40], maxZoom: 10 },
    );
  }, [points, map]);
  return null;
}

// ── Autocentrado al filtrar por CCAA/provincia ────────────────────────────────
// Al cambiar el trigger (filtros geográficos): si hay filtros activos vuela al
// bbox de los municipios visibles; si se limpian, vuelve a España completa.
// El bbox se calcula desde la GEOMETRÍA (el geojson no trae lat/lng en
// properties). Salta la primera ejecución (mount) para no pisar a <FitBounds>.
function FitBoundsFiltered({ filteredGeo, trigger }) {
  const map = useMap();
  const firstRun = useRef(true);
  useEffect(() => {
    if (firstRun.current) { firstRun.current = false; return; }
    if (trigger === '|') {
      map.flyTo([40.2, -3.7], 6, { duration: 0.8 });
      return;
    }
    const feats = filteredGeo?.features || [];
    if (!feats.length) return;
    let minLat = Infinity; let minLng = Infinity;
    let maxLat = -Infinity; let maxLng = -Infinity;
    const scan = (c) => {
      if (typeof c[0] === 'number') {
        const [lng, lat] = c;
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
        if (lng < minLng) minLng = lng;
        if (lng > maxLng) maxLng = lng;
        return;
      }
      for (const x of c) scan(x);
    };
    feats.forEach((f) => { if (f.geometry?.coordinates) scan(f.geometry.coordinates); });
    if (minLat === Infinity) return;
    map.flyToBounds(
      [[minLat, minLng], [maxLat, maxLng]],
      { padding: [40, 40], maxZoom: 12, duration: 0.8 },
    );
    // Reacciona SOLO al cambio de trigger (filtros geográficos), no a otros
    // cambios de filteredGeo (sector/rol/datos). Intencional.
  }, [trigger]);
  return null;
}

// ── Watcher de viewport ───────────────────────────────────────────────────────

function MapViewportTracker({ onBoundsChange }) {
  useMapEvents({
    moveend(e) {
      const b = e.target.getBounds();
      onBoundsChange({
        north: b.getNorth(), south: b.getSouth(),
        east: b.getEast(), west: b.getWest(), zoom: e.target.getZoom(),
      });
    },
  });
  return null;
}

// ── Captura ref del mapa ──────────────────────────────────────────────────────

function MapRefCapture({ mapRef }) {
  const map = useMap();
  useEffect(() => { mapRef.current = map; }, [map, mapRef]);
  return null;
}

// ── Texturas SVG por naturaleza jurídica ──────────────────────────────────────
// Genera un <pattern> SVG (color del grupo + textura según naturaleza) para
// inyectar en los <defs> del overlay de Leaflet. Los grupos privados usan color
// sólido (sin pattern), así que aquí solo se generan público / municipal / mixto.

const SVGNS = 'http://www.w3.org/2000/svg';

function crearPattern(grupo, color, naturaleza) {
  const pat = document.createElementNS(SVGNS, 'pattern');
  pat.setAttribute('id', `pat-${grupo}`);
  pat.setAttribute('width', '6');
  pat.setAttribute('height', '6');
  pat.setAttribute('patternUnits', 'userSpaceOnUse');

  const rect = document.createElementNS(SVGNS, 'rect');
  rect.setAttribute('width', '6');
  rect.setAttribute('height', '6');
  rect.setAttribute('fill', color);
  pat.appendChild(rect);

  if (naturaleza === 'publico') {
    // Rayas horizontales finas
    const line = document.createElementNS(SVGNS, 'line');
    line.setAttribute('x1', '0'); line.setAttribute('y1', '3');
    line.setAttribute('x2', '6'); line.setAttribute('y2', '3');
    line.setAttribute('stroke', 'white');
    line.setAttribute('stroke-width', '1.5');
    line.setAttribute('stroke-opacity', '0.4');
    pat.appendChild(line);
  } else if (naturaleza === 'municipal') {
    // Puntos
    const circle = document.createElementNS(SVGNS, 'circle');
    circle.setAttribute('cx', '3'); circle.setAttribute('cy', '3');
    circle.setAttribute('r', '1.2');
    circle.setAttribute('fill', 'white');
    circle.setAttribute('fill-opacity', '0.4');
    pat.appendChild(circle);
  } else if (naturaleza === 'mixto') {
    // Rayas diagonales
    const line = document.createElementNS(SVGNS, 'line');
    line.setAttribute('x1', '0'); line.setAttribute('y1', '6');
    line.setAttribute('x2', '6'); line.setAttribute('y2', '0');
    line.setAttribute('stroke', 'white');
    line.setAttribute('stroke-width', '1.5');
    line.setAttribute('stroke-opacity', '0.4');
    pat.appendChild(line);
  }
  return pat;
}

// Inyecta los patterns en el <defs> del SVG de Leaflet. El SVG del overlayPane
// solo existe cuando hay una capa vectorial montada, por eso se reintenta con
// un setTimeout de fallback y se re-asegura en zoomend/moveend.
function MapPatterns() {
  const map = useMap();
  useEffect(() => {
    const asegurarPatterns = () => {
      const svg = map.getPanes().overlayPane.querySelector('svg');
      if (!svg) return false;
      let defs = svg.querySelector('defs');
      if (!defs) {
        defs = document.createElementNS(SVGNS, 'defs');
        svg.insertBefore(defs, svg.firstChild);
      }
      GRUPOS_CON_TEXTURA.forEach((grupo) => {
        const naturaleza = naturalezaGrupo(grupo); // ya excluye privados
        if (defs.querySelector(`#pat-${grupo}`)) return; // ya existe
        defs.appendChild(crearPattern(grupo, grupoColor(grupo), naturaleza));
      });
      return true;
    };

    let timer = null;
    if (!asegurarPatterns()) timer = setTimeout(asegurarPatterns, 100);
    map.on('zoomend moveend', asegurarPatterns);
    return () => {
      if (timer) clearTimeout(timer);
      map.off('zoomend moveend', asegurarPatterns);
    };
  }, [map]);
  return null;
}

// ── Botón Filtros como control Leaflet ────────────────────────────────────────

function MapaFiltrosButton({ onClick }) {
  return (
    <div className="leaflet-top leaflet-left" style={{ marginTop: 70, zIndex: 1000 }}>
      <div className="leaflet-control leaflet-bar">
        <a href="#filtros" role="button" title="Abrir filtros"
          onClick={(e) => { e.preventDefault(); onClick(); }}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30 }}
        >
          <SlidersHorizontal size={14} />
        </a>
      </div>
    </div>
  );
}

// ── Buscador tipo Google Maps (fuera de MapContainer) ─────────────────────────

function MapaBuscador({ mapRef, apiData, onSelectMuni }) {
  const [query, setQuery]     = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen]       = useState(false);
  const debounceRef = useRef(null);
  const inputRef    = useRef(null);

  const search = useCallback(async (q) => {
    if (q.length < 3) { setResults([]); setOpen(false); return; }
    setLoading(true);
    try {
      const url = new URL(NOMINATIM);
      url.searchParams.set('q', q);
      url.searchParams.set('countrycodes', 'es');
      url.searchParams.set('format', 'json');
      url.searchParams.set('limit', '5');
      url.searchParams.set('addressdetails', '0');
      url.searchParams.set('email', 'contacto@inside-crm.com');
      const r = await fetch(url.toString(), { headers: { 'Accept-Language': 'es,ca' } });
      const data = await r.json();
      setResults(data);
      setOpen(data.length > 0);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (val.length < 3) { setResults([]); setOpen(false); return; }
    debounceRef.current = setTimeout(() => search(val), 1000);
  };

  const handleSelect = (item) => {
    const map = mapRef.current;
    if (!map) return;

    const bb = item.boundingbox;
    if (bb && bb.length === 4) {
      map.fitBounds(
        [[parseFloat(bb[0]), parseFloat(bb[2])], [parseFloat(bb[1]), parseFloat(bb[3])]],
        { maxZoom: 14 },
      );
    } else {
      map.setView([parseFloat(item.lat), parseFloat(item.lon)], 13);
    }

    // Buscar municipio coincidente en apiData
    const name = (item.name || item.display_name.split(',')[0]).toLowerCase().trim();
    const match = Object.values(apiData).find((m) => {
      const mn = (m.nombre || '').toLowerCase();
      return mn === name || mn.includes(name) || name.includes(mn);
    });
    if (match) onSelectMuni(match);

    setQuery(item.display_name.split(',')[0]);
    setOpen(false);
    setResults([]);
    inputRef.current?.blur();
  };

  return (
    <div
      style={{
        position: 'absolute', top: 10, left: 10,
        zIndex: 1002, width: 310,
      }}
    >
      <div className="bg-white rounded-lg shadow-lg border border-ink-200">
        {/* Input */}
        <div className="flex items-center gap-2 px-3 py-2">
          <Search size={14} className="text-ink-400 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            placeholder="Buscar municipio, dirección…"
            value={query}
            onChange={handleChange}
            onFocus={() => results.length > 0 && setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
            className="w-full text-sm text-ink-800 placeholder-ink-400 bg-transparent border-none outline-none"
          />
          {loading && <Loader2 size={13} className="animate-spin text-ink-400 shrink-0" />}
          {query && !loading && (
            <button
              onMouseDown={() => { setQuery(''); setResults([]); setOpen(false); }}
              className="text-ink-300 hover:text-ink-500 shrink-0"
            >
              <X size={13} />
            </button>
          )}
        </div>

        {/* Dropdown */}
        {open && results.length > 0 && (
          <div className="border-t border-ink-100">
            {results.map((item, i) => (
              <button
                key={i}
                className="w-full text-left px-3 py-2 text-xs hover:bg-ink-50 flex items-start gap-2"
                onMouseDown={() => handleSelect(item)}
              >
                <MapPin size={12} className="text-ink-400 mt-0.5 shrink-0" />
                <span className="text-ink-700 leading-snug line-clamp-2">
                  {item.display_name}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Panel municipio ───────────────────────────────────────────────────────────

const FUENTE_LABEL = {
  hacienda: 'Hacienda', 'sinac_v4.1': 'SINAC',
  agente_agua_v1: 'Agente IA', manual: 'Manual',
};
const FUENTE_CLS = {
  hacienda:       'bg-emerald-50 text-emerald-700',
  'sinac_v4.1':   'bg-blue-50 text-blue-700',
  agente_agua_v1: 'bg-violet-50 text-violet-700',
  manual:         'bg-amber-50 text-amber-700',
};
const NV_LABEL = {
  agente_verificado: 'IA verificado', manual_verificado: 'Manual', resuelto_fusion: 'Fusionado', resuelto_agente: 'IA resuelto', resuelto_manual: 'Resuelto',
  auto_clasificado: 'Auto', alias_confirmado: 'Alias', ekuis_inferred: 'EKUIS',
  conflicto_requires_review: 'Conflicto', requires_review: 'Pendiente',
};

function MuniPanel({ muni, onClose, modo }) {
  const navigate = useNavigate();

  const serviciosBaja = (muni.servicios_agua || [])
    .filter((s) => s.tipo === 'agua_potable_baja' || s.nivel === 'baja' || s.nivel === 'ambos');
  const serviciosAltaDistintos = (muni.servicios_agua || [])
    .filter((s) => (s.tipo === 'agua_potable_alta' || s.nivel === 'alta')
      && !serviciosBaja.some((b) => b.gestora_nombre === s.gestora_nombre));

  const mejorBaja = serviciosBaja.find(
    (s) => s.nivel_verificacion !== 'conflicto_requires_review',
  ) ?? serviciosBaja[0];
  const hasConflicto = mejorBaja?.nivel_verificacion === 'conflicto_requires_review';

  const tipoGestionLabel = mejorBaja
    ? (TIPO_GESTION_LABELS[mejorBaja.gestora_tipo] ?? mejorBaja.gestora_tipo ?? null)
    : null;

  return (
    <div
      style={{
        position: 'absolute',
        top: 8,
        right: 8,
        zIndex: 1001,
        width: 296,
        maxWidth: 'calc(100% - 16px)',
        maxHeight: 'calc(75vh - 24px)',
        overflowY: 'auto',
      }}
      className="bg-white rounded-lg shadow-xl border border-ink-200 p-4 space-y-2"
      data-testid="muni-panel"
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-semibold text-ink-900 text-sm leading-tight">{muni.nombre}</div>
          <div className="text-[11px] text-ink-500 mt-0.5">
            {muni.provincia} · INE {muni.codigo_ine}
            {muni.poblacion != null && ` · ${muni.poblacion.toLocaleString('es-ES')} hab.`}
          </div>
        </div>
        <button onClick={onClose} className="p-1 rounded hover:bg-ink-100 text-ink-400 shrink-0">
          <X size={14} />
        </button>
      </div>

      {hasConflicto && (
        <div className="flex items-center gap-1.5 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded px-2 py-1.5">
          <AlertTriangle size={12} className="shrink-0" />
          SINAC y Place discrepan — requiere revisión
        </div>
      )}

      {modo === 'mercado' ? (
        <div className="space-y-2 pt-1 border-t border-ink-100">
          {/* Gestor BAJA */}
          {mejorBaja ? (
            <div className="space-y-1">
              <div className="text-[10px] font-semibold text-ink-400 uppercase tracking-wider">
                Gestor BAJA
              </div>
              <div className="flex items-start gap-1.5 text-xs">
                <span className="mt-0.5 w-2.5 h-2.5 rounded-full shrink-0"
                  style={{ backgroundColor: grupoColor(mejorBaja.gestora_grupo) }} />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-ink-800 truncate">{mejorBaja.gestora_nombre || '—'}</div>
                  <div className="flex gap-1 mt-0.5 flex-wrap">
                    {mejorBaja.gestora_grupo && (
                      <span className="text-[10px] bg-ink-100 text-ink-600 px-1.5 py-0.5 rounded-full">
                        {GRUPO_LABELS[mejorBaja.gestora_grupo] ?? mejorBaja.gestora_grupo}
                      </span>
                    )}
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${FUENTE_CLS[mejorBaja.fuente] ?? 'bg-ink-100 text-ink-600'}`}>
                      {FUENTE_LABEL[mejorBaja.fuente] ?? mejorBaja.fuente}
                    </span>
                    {mejorBaja.nivel_verificacion && (
                      <span className="text-[10px] bg-ink-50 text-ink-500 px-1.5 py-0.5 rounded-full">
                        {NV_LABEL[mejorBaja.nivel_verificacion] ?? mejorBaja.nivel_verificacion}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : muni.gestora_nombre ? (
            <div className="flex items-start gap-1.5 text-xs">
              <Building2 size={13} className="text-ink-400 mt-0.5 shrink-0" />
              <div className="font-medium text-ink-800">{muni.gestora_nombre}</div>
            </div>
          ) : (
            <div className="text-xs text-ink-400 italic">Sin datos de gestora</div>
          )}

          {/* Gestor ALTA si difiere */}
          {serviciosAltaDistintos.length > 0 && (
            <div className="space-y-1">
              <div className="text-[10px] font-semibold text-ink-400 uppercase tracking-wider">Gestor ALTA</div>
              {serviciosAltaDistintos.slice(0, 1).map((s, i) => (
                <div key={i} className="flex items-start gap-1.5 text-xs">
                  <span className="mt-0.5 w-2.5 h-2.5 rounded-full shrink-0"
                    style={{ backgroundColor: grupoColor(s.gestora_grupo) }} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-ink-800 truncate">{s.gestora_nombre || '—'}</div>
                    {s.gestora_grupo && (
                      <span className="text-[10px] bg-ink-100 text-ink-600 px-1.5 py-0.5 rounded-full mt-0.5 inline-block">
                        {GRUPO_LABELS[s.gestora_grupo] ?? s.gestora_grupo}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {tipoGestionLabel && (
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-semibold text-ink-400 uppercase tracking-wider">Tipo</span>
              <span className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                {tipoGestionLabel}
              </span>
            </div>
          )}

          <div className="pt-1 border-t border-ink-100">
            <Button size="sm" variant="outline" className="text-xs h-7 px-2 w-full"
              onClick={() => toast.info('Próximamente — edición de gestor')}>
              <Pencil size={11} className="mr-1" /> Corregir dato
            </Button>
          </div>
        </div>
      ) : (
        <>
          {serviciosBaja.length > 0 ? (
            <div className="space-y-2 pt-1 border-t border-ink-100">
              {serviciosBaja.map((s, i) => (
                <div key={i} className="flex items-start gap-1.5 text-xs">
                  <Building2 size={13} className="text-ink-400 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-ink-800 truncate">{s.gestora_nombre || '—'}</div>
                    <div className="flex gap-1 mt-0.5 flex-wrap">
                      {s.gestora_grupo && (
                        <span className="text-[10px] bg-ink-100 text-ink-600 px-1.5 py-0.5 rounded-full">
                          {s.gestora_grupo}
                        </span>
                      )}
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${FUENTE_CLS[s.fuente] ?? 'bg-ink-100 text-ink-600'}`}>
                        {FUENTE_LABEL[s.fuente] ?? s.fuente}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : muni.gestora_nombre ? (
            <div className="flex items-start gap-1.5 text-xs pt-1 border-t border-ink-100">
              <Building2 size={13} className="text-ink-400 mt-0.5 shrink-0" />
              <div className="font-medium text-ink-800">{muni.gestora_nombre}</div>
            </div>
          ) : null}

          {muni.num_contratos > 0 && (
            <div className="space-y-1 pt-1 border-t border-ink-100">
              {(muni.contratos || []).slice(0, 3).map((c) => (
                <div key={c.contrato_id} className="text-[11px] text-ink-700 truncate">
                  {c.contrato_nombre || '(sin nombre)'}
                  {c.fecha_fin && <span className="text-ink-400"> · fin {String(c.fecha_fin).slice(0, 10)}</span>}
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-1.5 pt-1 border-t border-ink-100 flex-wrap">
            {muni.gestora_id && (
              <Button size="sm" variant="outline" className="text-xs h-7 px-2"
                onClick={() => navigate(`/clientes/${muni.gestora_id}`)}>
                <Eye size={11} className="mr-1" /> Ver ficha
              </Button>
            )}
            <Button size="sm" variant="outline" className="text-xs h-7 px-2"
              onClick={() => toast.info('Próximamente — reportar visita desde el mapa')}>
              <CalendarPlus size={11} className="mr-1" /> Visita
            </Button>
            <Button size="sm" variant="outline" className="text-xs h-7 px-2"
              onClick={() => navigate('/tareas')}>
              <ClipboardList size={11} className="mr-1" /> Tarea
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

// ── Leyenda inline dinámica ───────────────────────────────────────────────────

function fmtHab(n) {
  if (n >= 1000000) return `${(n / 1000000).toLocaleString('es-ES', { maximumFractionDigits: 1 })}M`;
  if (n >= 1000) return `${Math.round(n / 1000).toLocaleString('es-ES')}k`;
  return n.toLocaleString('es-ES');
}

function pctStr(n, total) {
  return `${((n / total) * 100).toLocaleString('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
}

// ── Clasificación comercial (filtros rápidos modo MERCADO) ───────────────────

const FILTROS_COMERCIALES = [
  { v: 'todos',                 l: 'Todos' },
  { v: 'gestion_directa',       l: 'Gestión directa' },
  { v: 'empresa_publica_local', l: 'Empresa pública local' },
  { v: 'supramunicipal',        l: 'Entidad supramunicipal' },
  { v: 'privada_mixta',         l: 'Operadora privada/mixta' },
  { v: 'ute',                   l: 'UTE' },
];

const GRUPOS_SUPRAMUNICIPALES = [
  'canal_isabel_ii', 'giahsa', 'acosol', 'aljarafesa', 'catsa', 'atll', 'mcp_pamplona', 'emasesa',
];
const GRUPOS_PRIVADA_MIXTA = [
  'veolia', 'aqualia', 'acciona', 'global_omnium', 'facsa', 'saur', 'sacyr',
  'aqlara', 'aigues_terrassa',
];

// Devuelve la categoría comercial de un municipio según los campos del endpoint
// /mapa (grupo_empresarial, tipo_gestion_baja, gestora_nombre). Orden de
// evaluación: las reglas más específicas y los grupos supramunicipales se
// comprueban antes que "empresa pública local" para evitar clasificar como
// local a operadores supramunicipales públicos (p.ej. Canal Isabel II).
function clasificarMunicipio(m) {
  if (!m) return null;
  const grupo  = m.grupo_empresarial || '';
  const tipo   = m.tipo_gestion_baja || '';
  const nombre = m.gestora_nombre || '';

  if (/\bUTE\b/i.test(nombre) || m.es_ute === true) return 'ute';
  if (grupo === 'municipal' && tipo === 'directa_municipal') return 'gestion_directa';
  if (tipo === 'mancomunidad' || tipo === 'consorcio' || GRUPOS_SUPRAMUNICIPALES.includes(grupo)) return 'supramunicipal';
  if (tipo === 'empresa_publica' || tipo === 'organismo_autonomo') return 'empresa_publica_local';
  if (tipo === 'concesion' || tipo === 'empresa_mixta' || GRUPOS_PRIVADA_MIXTA.includes(grupo)) return 'privada_mixta';
  return null;
}

// ── Filtros de mercado del agua (Sprint 4A, aplicados en frontend) ───────────

// Sector del agua: filtra por el tipo de servicio presente en servicios_agua.
function filtrarPorSector(m, sectorAgua) {
  if (sectorAgua === 'todos') return true;
  const servicios = m?.servicios_agua || [];
  if (sectorAgua === 'ciclo_integral') return servicios.some((s) => s.tipo === 'ciclo_integral');
  if (sectorAgua === 'alta') return servicios.some((s) => s.tipo === 'agua_potable_alta');
  if (sectorAgua === 'baja') return servicios.some((s) => s.tipo === 'agua_potable_baja');
  if (sectorAgua === 'alta_baja') return servicios.some((s) => s.tipo === 'agua_potable_alta') && servicios.some((s) => s.tipo === 'agua_potable_baja');
  if (sectorAgua === 'saneamiento') return servicios.some((s) => s.tipo === 'saneamiento');
  return true;
}

// [INFERENCIA Sprint 4A] El spec no define el mapeo dato→naturaleza/rol del
// operador. Se deriva de la taxonomía existente (tipo_gestion_baja, grupo,
// es_ute). REVISABLE: ajustar cuando Marc valide la semántica de negocio.
function naturalezaOperadorDe(m) {
  if (!m) return null;
  const tipo  = m.tipo_gestion_baja || '';
  const grupo = m.grupo_empresarial || '';
  if (tipo === 'empresa_mixta') return 'mixto';
  if (tipo === 'concesion' || GRUPOS_PRIVADA_MIXTA.includes(grupo)) return 'privado';
  if (
    ['directa_municipal', 'mancomunidad', 'consorcio', 'empresa_publica', 'organismo_autonomo'].includes(tipo)
    || GRUPOS_SUPRAMUNICIPALES.includes(grupo)
    || grupo === 'municipal'
  ) return 'publico';
  return null;
}

function rolesOperadorDe(m) {
  if (!m) return [];
  const tipo   = m.tipo_gestion_baja || '';
  const nombre = m.gestora_nombre || '';
  const roles  = [];
  if (/\bUTE\b/i.test(nombre) || m.es_ute === true) roles.push('ute');
  if (tipo === 'directa_municipal') roles.push('operador_titular');
  if (tipo === 'concesion') roles.push('concesionado');
  if (tipo === 'empresa_publica' || tipo === 'organismo_autonomo') roles.push('empresa_publica_op');
  if (tipo === 'empresa_mixta') roles.push('empresa_mixta');
  if (naturalezaOperadorDe(m) === 'publico') roles.push('titular_publico');
  return roles;
}

// ── Dashboard analítico de mercado (bajo el mapa) ─────────────────────────────

// Naturaleza del operador derivada del grupo empresarial (solo display en tabla).
const NATURALEZA_GRUPO = {
  // Pública (verde)
  municipal: 'publica', canal_isabel_ii: 'publica', independiente_publico: 'publica',
  emasesa: 'publica', giahsa: 'publica', acosol: 'publica', aljarafesa: 'publica',
  catsa: 'publica', atll: 'publica', mcp_pamplona: 'publica', sumem: 'publica',
  aigues_terrassa: 'publica',
  // Privada (naranja)
  veolia: 'privada', aqualia: 'privada', acciona: 'privada', global_omnium: 'privada',
  facsa: 'privada', saur: 'privada', sacyr: 'privada', aqlara: 'privada',
  independiente_privado: 'privada',
  // Municipal (gris) — si algún municipio trae el grupo directa_municipal
  directa_municipal: 'municipal',
};
const NAT_BADGE = {
  publica:   { label: 'Pública',   cls: 'bg-emerald-50 text-emerald-700' },
  privada:   { label: 'Privada',   cls: 'bg-orange-50 text-orange-700' },
  municipal: { label: 'Municipal', cls: 'bg-ink-100 text-ink-600' },
};

// Columnas ordenables: a qué campo numérico/alfabético mapea cada una.
const SORT_FIELD = {
  operador: 'operador', municipios: 'mun', pct_mun: 'mun',
  poblacion: 'pob', pct_hab: 'pob',
};

// Calcula KPIs y reparto por operador SOLO sobre los municipios visibles
// (los presentes en filteredGeo), cruzando cada feature con apiData.
const DRILLDOWN_MAX = 50;

function DashboardMercado({ apiData, filteredGeo, modo, totalEspana }) {
  const [sortBy, setSortBy]   = useState('municipios');
  const [sortDir, setSortDir] = useState('desc');
  const [expandedGrupo, setExpandedGrupo] = useState(null);

  const stats = useMemo(() => {
    const features = filteredGeo?.features || [];
    const porGrupo = {};
    let totalMun = 0;
    let totalPob = 0;
    for (const f of features) {
      const m = apiData[String(f.id)];
      if (!m) continue;
      const grupo = m.grupo_empresarial || 'sin_datos';
      const pob = m.poblacion || 0;
      if (!porGrupo[grupo]) porGrupo[grupo] = { mun: 0, pob: 0 };
      porGrupo[grupo].mun += 1;
      porGrupo[grupo].pob += pob;
      totalMun += 1;
      totalPob += pob;
    }
    const filas = Object.entries(porGrupo)
      .map(([grupo, v]) => ({ grupo, ...v }))
      .filter((r) => r.mun > 0);
    const operadores = filas.filter((r) => r.grupo !== 'sin_datos').length;
    return { totalMun, totalPob, operadores, filas };
  }, [apiData, filteredGeo]);

  // % mun y % hab comparten orden con mun y pob (los totales son fijos por render).
  const filasOrdenadas = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    const campo = SORT_FIELD[sortBy] ?? 'mun';
    return [...stats.filas].sort((a, b) => {
      if (campo === 'operador') {
        const av = (GRUPO_LABELS[a.grupo] ?? 'Sin datos').toLowerCase();
        const bv = (GRUPO_LABELS[b.grupo] ?? 'Sin datos').toLowerCase();
        if (av < bv) return -dir;
        if (av > bv) return dir;
        return 0;
      }
      return (a[campo] - b[campo]) * dir;
    });
  }, [stats.filas, sortBy, sortDir]);

  // Drill-down: municipios visibles del grupo expandido, ordenados por habitantes.
  const expandedMunis = useMemo(() => {
    if (!expandedGrupo) return [];
    const features = filteredGeo?.features || [];
    const list = [];
    for (const f of features) {
      const m = apiData[String(f.id)];
      if (!m) continue;
      if ((m.grupo_empresarial || 'sin_datos') === expandedGrupo) list.push(m);
    }
    list.sort((a, b) => (b.poblacion || 0) - (a.poblacion || 0));
    return list;
  }, [expandedGrupo, filteredGeo, apiData]);

  if (modo !== 'mercado') return null;

  const { totalMun, totalPob, operadores } = stats;
  const denomTotal = totalEspana || Object.keys(apiData).length || 1;

  const handleSort = (col) => {
    if (sortBy === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(col); setSortDir('desc'); }
  };
  const toggleGrupo = (grupo) => setExpandedGrupo((g) => (g === grupo ? null : grupo));
  const ind = (col) => (sortBy === col ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '');
  const THSORT = 'py-1.5 px-2 font-medium cursor-pointer select-none hover:text-ink-600';

  return (
    <Card className="p-4 -mx-4 lg:-mx-6 mt-2" data-testid="dashboard-mercado">
      <h2 className="text-sm font-semibold text-ink-900 mb-3">Análisis de mercado</h2>

      {/* KPIs */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="rounded-lg border border-ink-100 bg-ink-50/50 p-3">
          <div className="text-[10px] font-semibold text-ink-400 uppercase tracking-wider">Municipios visibles</div>
          <div className="text-xl font-semibold text-ink-900 tabular-nums">{totalMun.toLocaleString('es-ES')}</div>
          <div className="text-[11px] text-ink-400">{pctStr(totalMun, denomTotal)} del total España</div>
        </div>
        <div className="rounded-lg border border-ink-100 bg-ink-50/50 p-3">
          <div className="text-[10px] font-semibold text-ink-400 uppercase tracking-wider">Población total</div>
          <div className="text-xl font-semibold text-ink-900 tabular-nums">{fmtHab(totalPob)}</div>
          <div className="text-[11px] text-ink-400">habitantes</div>
        </div>
        <div className="rounded-lg border border-ink-100 bg-ink-50/50 p-3">
          <div className="text-[10px] font-semibold text-ink-400 uppercase tracking-wider">Operadores presentes</div>
          <div className="text-xl font-semibold text-ink-900 tabular-nums">{operadores}</div>
          <div className="text-[11px] text-ink-400">grupos distintos</div>
        </div>
      </div>

      {/* Tabla de operadores */}
      {filasOrdenadas.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-ink-400 border-b border-ink-100">
                <th className={`${THSORT} text-left`} onClick={() => handleSort('operador')}>Operador{ind('operador')}</th>
                <th className="py-1.5 px-2 font-medium text-left">Naturaleza</th>
                <th className={`${THSORT} text-right`} onClick={() => handleSort('municipios')}>Municipios{ind('municipios')}</th>
                <th className={`${THSORT} text-right`} onClick={() => handleSort('pct_mun')}>% mun{ind('pct_mun')}</th>
                <th className={`${THSORT} text-right`} onClick={() => handleSort('poblacion')}>Población{ind('poblacion')}</th>
                <th className={`${THSORT} text-right`} onClick={() => handleSort('pct_hab')}>% hab{ind('pct_hab')}</th>
              </tr>
            </thead>
            <tbody>
              {filasOrdenadas.map((r) => {
                const nat = NATURALEZA_GRUPO[r.grupo];
                const badge = nat ? NAT_BADGE[nat] : null;
                const isExpanded = expandedGrupo === r.grupo;
                return (
                  <React.Fragment key={r.grupo}>
                    <tr
                      className="border-b border-ink-50 cursor-pointer hover:bg-ink-50/60"
                      onClick={() => toggleGrupo(r.grupo)}
                    >
                      <td className="py-1.5 pr-2">
                        <span className="flex items-center gap-1.5">
                          <span className="text-ink-400 w-2.5 shrink-0">{isExpanded ? '▼' : '▶'}</span>
                          <span className="w-3 h-3 rounded-sm shrink-0"
                            style={{ backgroundColor: GRUPO_COLORS[r.grupo] ?? '#E5E7EB' }} />
                          <span className="text-ink-700">{GRUPO_LABELS[r.grupo] ?? 'Sin datos'}</span>
                        </span>
                      </td>
                      <td className="py-1.5 px-2">
                        {badge ? (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${badge.cls}`}>{badge.label}</span>
                        ) : (
                          <span className="text-ink-300">—</span>
                        )}
                      </td>
                      <td className="py-1.5 px-2 text-right tabular-nums text-ink-700">{r.mun.toLocaleString('es-ES')}</td>
                      <td className="py-1.5 px-2 text-right tabular-nums text-ink-400">{pctStr(r.mun, totalMun)}</td>
                      <td className="py-1.5 px-2 text-right tabular-nums text-ink-700">{fmtHab(r.pob)}</td>
                      <td className="py-1.5 pl-2 text-right tabular-nums text-ink-400">{pctStr(r.pob, totalPob || 1)}</td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={6} className="p-0">
                          <div className="max-h-64 overflow-y-auto bg-slate-50 border-y border-ink-100">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-ink-400">
                                  <th className="py-1 px-2 font-medium text-left">Municipio</th>
                                  <th className="py-1 px-2 font-medium text-left">Provincia</th>
                                  <th className="py-1 px-2 font-medium text-left">Gestora</th>
                                  <th className="py-1 px-2 font-medium text-right">Habitantes</th>
                                </tr>
                              </thead>
                              <tbody>
                                {expandedMunis.slice(0, DRILLDOWN_MAX).map((m) => (
                                  <tr key={m.codigo_ine} className="border-t border-ink-100/60">
                                    <td className="py-1 px-2 text-ink-700">{m.nombre}</td>
                                    <td className="py-1 px-2 text-ink-500">{m.provincia || '—'}</td>
                                    <td className="py-1 px-2 text-ink-500 truncate max-w-[180px]">{m.gestora_nombre || '—'}</td>
                                    <td className="py-1 px-2 text-right tabular-nums text-ink-700">
                                      {(m.poblacion ?? 0).toLocaleString('es-ES')}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            {expandedMunis.length > DRILLDOWN_MAX && (
                              <div className="py-1.5 px-2 text-[11px] text-ink-400 italic">
                                y {(expandedMunis.length - DRILLDOWN_MAX).toLocaleString('es-ES')} más…
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-xs text-ink-400 italic">No hay municipios visibles con los filtros actuales.</div>
      )}
    </Card>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────

export default function MapaComercial360() {
  const navigate = useNavigate();
  const isMobile = () => typeof window !== 'undefined' && window.innerWidth < 768;
  const leafletMapRef = useRef(null);

  // [COMERCIAL desconectado] El modo COMERCIAL está vacío; se fija a 'mercado'.
  // Reconectar: const [modo, setModo] = useState('mercado'); y restaurar el toggle.
  const modo = 'mercado';
  const [filtroComercial, setFiltroComercial] = useState('todos');

  const [geoJson, setGeoJson]           = useState(null);
  const [geoLoading, setGeoLoading]     = useState(true);
  const [apiData, setApiData]           = useState({});
  const [muniLoading, setMuniLoading]   = useState(true);
  const [muniStats, setMuniStats]       = useState({ total: 0, cubiertos: 0, sin_contrato: 0 });
  const [selectedMuni, setSelectedMuni] = useState(null);
  const [countsPorRango, setCountsPorRango] = useState({});
  const [pobPorGrupo, setPobPorGrupo] = useState({});

  const [entityMarkers, setEntityMarkers]   = useState([]);
  const [entityStats, setEntityStats]       = useState({ total: 0, activos: 0, leads: 0, tareas_vencidas: 0 });
  const [entityLoading, setEntityLoading]   = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [entityResumen, setEntityResumen]   = useState(null);
  const [resumenLoading, setResumenLoading] = useState(false);
  const [capasActivas, setCapasActivas]     = useState(['clientes_platecsa', 'leads']);
  const [soloMisEntidades, setSoloMisEntidades] = useState(false);

  const [pais] = useState('ES'); // país aparcado en ES (sin selector); PT pendiente
  const [tamano, setTamano]           = useState('');
  const [conContrato, setConContrato] = useState('');
  // Filtro geográfico múltiple (client-side, Opción A).
  const [ccaasFilter, setCcaasFilter]           = useState([]);
  const [provinciasFilter, setProvinciasFilter] = useState([]);

  const [gruposFilter, setGruposFilter] = useState([]);
  const [rangoFilter, setRangoFilter]   = useState([]);
  const [zonaFilter, setZonaFilter]     = useState('');
  const [zonas, setZonas]               = useState([]);

  // ── Sprint 4A: capas de delimitación + filtros de mercado del agua ──────────
  const [capaProvincias, setCapaProvincias] = useState(false);
  const [capaCCAA, setCapaCCAA]             = useState(false);
  const [capaZonas, setCapaZonas]           = useState(false);
  const [sectorAgua, setSectorAgua]         = useState('todos');
  const [rolOperador, setRolOperador]       = useState([]);
  const [naturalezaOperador, setNaturalezaOperador] = useState([]);
  const [geodataCCAA, setGeodataCCAA]               = useState(null);
  const [geodataProvincias, setGeodataProvincias]   = useState(null);

  // Entidades CRM desconectadas del mapa (Sprint 4A). Reconectar = poner a true.
  const crmEntitiesEnabled = false;

  const [filtrosOpen, setFiltrosOpen] = useState(false);
  const [viewport, setViewport]       = useState(null);
  const vpTimer = useRef(null);

  const grupos = pais === 'ES' ? CCAA_ES : REGIONES_PT;
  const ccaaOptions = Object.keys(grupos);
  // Provincias seleccionables: las de las CCAA elegidas, o todas si no hay ninguna.
  const provinciaOptions = ccaasFilter.length
    ? ccaasFilter.flatMap((c) => grupos[c] || [])
    : Object.values(grupos).flat();

  // Grupos visibles en modo MERCADO — calculado desde apiData
  const gruposVisibles = useMemo(() => {
    if (modo !== 'mercado' || !Object.keys(apiData).length) return null;
    const counts = {};
    for (const m of Object.values(apiData)) {
      const g = m.grupo_empresarial ?? 'sin_datos';
      counts[g] = (counts[g] || 0) + 1;
    }
    return counts;
  }, [apiData, modo]);

  useEffect(() => {
    fetch(GEO_URL)
      .then((r) => r.json())
      .then((data) => { setGeoJson(data); setGeoLoading(false); })
      .catch(() => { toast.error('No se pudo cargar el mapa de polígonos'); setGeoLoading(false); });
  }, []);

  // Zonas comerciales accesibles para el filtro del drawer (carga única al montar).
  useEffect(() => {
    req('/api/zonas/disponibles-mapa')
      .then((r) => setZonas(Array.isArray(r) ? r : []))
      .catch(() => setZonas([]));
  }, []);

  // Carga lazy de límites administrativos: solo se descargan al activar la capa.
  useEffect(() => {
    if (capaCCAA && !geodataCCAA) {
      req('/api/municipios/geo/ccaa')
        .then(setGeodataCCAA)
        .catch(() => toast.error('No se pudieron cargar los límites de CCAA'));
    }
  }, [capaCCAA, geodataCCAA]);

  useEffect(() => {
    if (capaProvincias && !geodataProvincias) {
      req('/api/municipios/geo/provincias')
        .then(setGeodataProvincias)
        .catch(() => toast.error('No se pudieron cargar los límites de provincias'));
    }
  }, [capaProvincias, geodataProvincias]);

  const cargarMunicipios = useCallback(async () => {
    setMuniLoading(true);
    try {
      const qs = new URLSearchParams();
      qs.set('pais', pais);
      if (zonaFilter) qs.set('zona_id', zonaFilter);
      if (conContrato) qs.set('con_contrato', conContrato);
      if (tamano) qs.set('tamano', tamano);
      gruposFilter.forEach((g) => qs.append('grupos_empresariales', g));
      rangoFilter.forEach((r) => qs.append('rango_poblacion', r));
      qs.set('include_counts', 'true');
      qs.set('limit', 10000);
      const r = await req(`/api/municipios/mapa?${qs.toString()}`);
      const map = {};
      for (const m of (r.items || [])) map[m.codigo_ine] = m;
      setApiData(map);
      setMuniStats({ total: r.total, cubiertos: r.cubiertos, sin_contrato: r.sin_contrato });
      setCountsPorRango(r.counts_por_rango || {});
      setPobPorGrupo(r.poblacion_por_grupo || {});
    } catch (e) { toast.error(e.message); }
    finally { setMuniLoading(false); }
  }, [pais, zonaFilter, conContrato, tamano, gruposFilter, rangoFilter]);

  useEffect(() => { cargarMunicipios(); }, [cargarMunicipios]);
  // Al ocultar la capa de zonas, limpiar el filtro de zona: deseleccionar la
  // zona debe recargar el mapa con todos los municipios (vía cargarMunicipios).
  useEffect(() => {
    if (!capaZonas && zonaFilter) setZonaFilter('');
  }, [capaZonas, zonaFilter]);

  const cargarEntidades = useCallback(async (vp) => {
    if (!vp || capasActivas.length === 0) { setEntityMarkers([]); return; }
    setEntityLoading(true);
    try {
      const qs = new URLSearchParams({
        north: vp.north, south: vp.south, east: vp.east, west: vp.west, zoom: vp.zoom,
        solo_mis_entidades: soloMisEntidades,
      });
      capasActivas.forEach((c) => qs.append('capas', c));
      const r = await req(`/api/mapa-comercial/viewport?${qs.toString()}`);
      setEntityMarkers(r.markers || []);
      setEntityStats(r.stats || {});
    } catch (e) { toast.error(e.message); }
    finally { setEntityLoading(false); }
  }, [capasActivas, soloMisEntidades]);

  const handleBoundsChange = useCallback((bounds) => {
    if (vpTimer.current) clearTimeout(vpTimer.current);
    vpTimer.current = setTimeout(() => setViewport(bounds), 400);
  }, []);

  useEffect(() => {
    if (crmEntitiesEnabled && viewport) cargarEntidades(viewport);
  }, [viewport, cargarEntidades, crmEntitiesEnabled]);

  // Carga inicial en modo comercial antes de que el usuario mueva el mapa
  useEffect(() => {
    if (crmEntitiesEnabled && modo === 'comercial' && !viewport) {
      cargarEntidades({ north: 43.8, south: 35.9, east: 4.3, west: -9.3, zoom: 6 });
    }
  }, [modo, viewport, cargarEntidades, crmEntitiesEnabled]);

  const handleEntityClick = useCallback(async (marker) => {
    setSelectedMuni(null);
    setSelectedEntity(marker);
    setEntityResumen(null);
    setResumenLoading(true);
    try {
      const r = await req(`/api/mapa-comercial/entidad/${marker.id}/resumen`);
      setEntityResumen(r);
    } catch (e) { toast.error(e.message); }
    finally { setResumenLoading(false); }
  }, []);

  const handleCercaDeMi = useCallback(async (lat, lng) => {
    try {
      const r = await req(`/api/mapa-comercial/cerca-de-mi?lat=${lat}&lng=${lng}&radio_km=25`);
      if (!r.items?.length) { toast.info('No hay entidades en un radio de 25 km'); return; }
      setEntityMarkers(r.items);
      setEntityStats({ total: r.total, activos: 0, leads: 0, tareas_vencidas: 0 });
      toast.success(`${r.total} entidades en 25 km — ordenadas por prioridad`);
    } catch (e) { toast.error(e.message); }
  }, []);

  const toggleCapa = useCallback((id) => {
    setCapasActivas((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id],
    );
  }, []);

  const filteredGeo = useMemo(() => {
    if (!geoJson || !Object.keys(apiData).length) return null;
    const ines = new Set(Object.keys(apiData));
    const aplicarComercial = modo === 'mercado' && filtroComercial !== 'todos';
    const aplicarMercado = modo === 'mercado';
    return {
      type: 'FeatureCollection',
      features: geoJson.features.filter((f) => {
        const ine = String(f.id);
        if (!ines.has(ine)) return false;
        const m = apiData[ine];
        // Filtro geográfico múltiple (client-side). provOptDe normaliza el
        // nombre bilingüe a la provincia canónica de CCAA_ES.
        if (ccaasFilter.length || provinciasFilter.length) {
          const prov = provOptDe(m);
          if (provinciasFilter.length && !provinciasFilter.includes(prov)) return false;
          if (ccaasFilter.length && !ccaasFilter.includes(PROV_TO_CCAA[prov])) return false;
        }
        if (aplicarComercial && clasificarMunicipio(m) !== filtroComercial) return false;
        if (aplicarMercado) {
          if (!filtrarPorSector(m, sectorAgua)) return false;
          if (rolOperador.length) {
            const roles = rolesOperadorDe(m);
            if (!rolOperador.some((r) => roles.includes(r))) return false;
          }
          if (naturalezaOperador.length && !naturalezaOperador.includes(naturalezaOperadorDe(m))) return false;
        }
        return true;
      }),
    };
  }, [geoJson, apiData, modo, filtroComercial, sectorAgua, rolOperador, naturalezaOperador, ccaasFilter, provinciasFilter]);

  const clientesPuntos = useMemo(
    () => Object.values(apiData).filter((m) => m.num_contratos > 0 && m.lat && m.lng),
    [apiData],
  );

  const styleFeature = useCallback((feature) => {
    const m = apiData[String(feature.id)];
    let fillColor;
    if (modo === 'mercado') {
      const grupo = m?.grupo_empresarial;
      // Color por operador + textura por naturaleza jurídica (privado = sólido).
      fillColor = naturalezaGrupo(grupo) === 'privado'
        ? grupoColor(grupo)
        : `url(#pat-${grupo})`;
    } else {
      fillColor = m?.color_estado ?? '#E5E7EB';
    }
    return { fillColor, fillOpacity: m ? 0.70 : 0.1, color: '#ffffff', weight: 0.7 };
  }, [apiData, modo]);

  const onEachFeature = useCallback((feature, layer) => {
    const ine = String(feature.id);
    const m   = apiData[ine];
    layer.on({
      click:     () => { setSelectedEntity(null); setEntityResumen(null); setResumenLoading(false); setSelectedMuni(m || { codigo_ine: ine, ...feature.properties }); },
      mouseover: () => layer.setStyle({ weight: 2, fillOpacity: 0.88 }),
      mouseout:  () => layer.setStyle(styleFeature(feature)),
    });
  }, [apiData, styleFeature]);

  const geoKey = `${pais}|${ccaasFilter.join(',')}|${provinciasFilter.join(',')}|${zonaFilter}|${conContrato}|${tamano}|${modo}|${filtroComercial}|${sectorAgua}|${rolOperador.join(',')}|${naturalezaOperador.join(',')}|${gruposFilter.join(',')}|${rangoFilter.join(',')}|${muniStats.total}`;
  const isMapReady = !geoLoading && !muniLoading && filteredGeo;

  const showEntityPanel = entityResumen && !isMobile();
  const showBottomSheet = (entityResumen || resumenLoading) && isMobile();

  return (
    <div className="space-y-2" data-testid="mapa-comercial-360">
      {/* Cabecera */}
      <div className="flex items-center gap-3 min-w-0 flex-wrap">
        <MapIcon className="w-5 h-5 text-brand-500 shrink-0" />
        <h1 className="text-lg font-semibold text-ink-900 shrink-0">Mapa Comercial 360</h1>

        {/* [COMERCIAL desconectado] Toggle MERCADO/COMERCIAL — reconectar cuando
            el modo COMERCIAL tenga contenido (requiere volver modo a useState):
        <div className="flex rounded-lg border border-ink-200 overflow-hidden shrink-0">
          <button
            className={`px-3 py-1 text-xs font-semibold tracking-wide transition-colors ${
              modo === 'mercado' ? 'bg-brand-600 text-white' : 'bg-white text-ink-500 hover:bg-ink-50'
            }`}
            onClick={() => setModo('mercado')}
          >
            MERCADO
          </button>
          <button
            className={`px-3 py-1 text-xs font-semibold tracking-wide transition-colors border-l border-ink-200 ${
              modo === 'comercial' ? 'bg-brand-600 text-white' : 'bg-white text-ink-500 hover:bg-ink-50'
            }`}
            onClick={() => setModo('comercial')}
          >
            COMERCIAL
          </button>
        </div>
        */}

        <span className="text-sm text-ink-400 truncate">
          {muniStats.total.toLocaleString('es-ES')} municipios
          {entityMarkers.length > 0 && ` · ${entityMarkers.length} entidades en vista`}
        </span>
      </div>

      {/* Filtros rápidos de clasificación comercial (solo MERCADO) */}
      {modo === 'mercado' && (
        <div className="flex flex-wrap gap-1.5">
          {FILTROS_COMERCIALES.map(({ v, l }) => (
            <button
              key={v}
              type="button"
              onClick={() => setFiltroComercial(v)}
              className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${
                filtroComercial === v
                  ? 'bg-brand-600 text-white border-brand-600'
                  : 'bg-white text-ink-600 border-ink-200 hover:bg-ink-50'
              }`}
            >
              {l}
            </button>
          ))}
        </div>
      )}

      {/* Mapa */}
      <Card className="p-0 relative -mx-4 lg:-mx-6" data-testid="mapa-comercial-container">
        {(geoLoading || (muniLoading && !filteredGeo)) ? (
          <div className="flex items-center justify-center flex-col gap-2"
            style={{ height: 'calc(55vh)', minHeight: 400 }}>
            <Loader2 className="w-6 h-6 animate-spin text-ink-400" />
            <p className="text-sm text-ink-400">
              {geoLoading ? 'Cargando polígonos IGN…' : 'Cargando datos de municipios…'}
            </p>
          </div>
        ) : (
          <div className="relative">
            <MapContainer
              center={[40.2, -3.7]}
              zoom={6}
              style={{ height: 'calc(55vh)', minHeight: 400, width: '100%' }}
              scrollWheelZoom
              zoomControl={false}
            >
              <TileLayer
                attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {/* Zoom reubicado a bottom-right para no chocar con el buscador top-left (FIX Sprint 4A) */}
              <ZoomControl position="bottomright" />
              <FitBounds points={clientesPuntos} />
              <FitBoundsFiltered
                filteredGeo={filteredGeo}
                trigger={`${ccaasFilter.join(',')}|${provinciasFilter.join(',')}`}
              />
              <MapViewportTracker onBoundsChange={handleBoundsChange} />
              <MapRefCapture mapRef={leafletMapRef} />
              <MapPatterns />
              <MapaLocateButton onCercaDeMi={handleCercaDeMi} />
              <MapaFiltrosButton onClick={() => setFiltrosOpen(true)} />

              {/* Capas de delimitación administrativa (Sprint 4A) */}
              {capaProvincias && geodataProvincias && (
                <GeoJSON
                  key="geo-provincias"
                  data={geodataProvincias}
                  style={{ color: '#9CA3AF', weight: 1, fill: false, opacity: 0.6 }}
                  interactive={false}
                />
              )}
              {capaCCAA && geodataCCAA && (
                <GeoJSON
                  key="geo-ccaa"
                  data={geodataCCAA}
                  style={{ color: '#1E3A5F', weight: 2, fill: false, opacity: 0.8 }}
                  interactive={false}
                />
              )}

              {/* Municipios (polígonos) — capa única, sin LayersControl (FIX Sprint 4A) */}
              {isMapReady && (
                <GeoJSON key={geoKey} data={filteredGeo}
                  style={styleFeature} onEachFeature={onEachFeature} />
              )}

              {/* Entidades CRM desconectadas (Sprint 4A); reconectar con crmEntitiesEnabled */}
              {crmEntitiesEnabled && entityMarkers.map((e) => (
                <MapaEntityMarker key={e.id} entity={e} onClick={handleEntityClick} />
              ))}
            </MapContainer>

            {/* Buscador — sobre el mapa, top-left (FIX Sprint 4A) */}
            <MapaBuscador
              mapRef={leafletMapRef}
              apiData={apiData}
              onSelectMuni={(m) => { setSelectedEntity(null); setEntityResumen(null); setSelectedMuni(m); }}
            />

            {/* Stats pills — desplazados bajo el buscador */}
            {crmEntitiesEnabled && modo === 'comercial' && entityMarkers.length > 0 && (
              <div style={{ position: 'absolute', top: 62, left: '50%', transform: 'translateX(-50%)', zIndex: 999 }}>
                <MapaStatsBar stats={entityStats} />
              </div>
            )}

            {crmEntitiesEnabled && modo === 'comercial' && entityLoading && (
              <div className="absolute top-2 left-1/2 -translate-x-1/2 z-[1000] bg-white/90 rounded-full px-3 py-1 flex items-center gap-1.5 shadow text-xs text-ink-600">
                <Loader2 size={12} className="animate-spin" /> Cargando entidades…
              </div>
            )}

            {selectedMuni && !selectedEntity && (
              <MuniPanel muni={selectedMuni} onClose={() => setSelectedMuni(null)} modo={modo} />
            )}

            {showEntityPanel && (
              <MapaEntityPanel
                resumen={entityResumen}
                onClose={() => { setSelectedEntity(null); setEntityResumen(null); }}
              />
            )}

            {resumenLoading && !isMobile() && (
              <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 1001, width: 288 }}
                className="bg-white rounded-lg shadow-xl border border-ink-200 p-6 flex items-center justify-center gap-2 text-ink-400 text-sm">
                <Loader2 size={16} className="animate-spin" /> Cargando ficha…
              </div>
            )}

            <MapaDrawerFiltros
              isOpen={filtrosOpen}
              onClose={() => setFiltrosOpen(false)}
              modo={modo}
              capaProvincias={capaProvincias} setCapaProvincias={setCapaProvincias}
              capaCCAA={capaCCAA} setCapaCCAA={setCapaCCAA}
              capaZonas={capaZonas} setCapaZonas={setCapaZonas}
              sectorAgua={sectorAgua} setSectorAgua={setSectorAgua}
              rolOperador={rolOperador} setRolOperador={setRolOperador}
              naturalezaOperador={naturalezaOperador} setNaturalezaOperador={setNaturalezaOperador}
              gruposFilter={gruposFilter} setGruposFilter={setGruposFilter}
              rangoFilter={rangoFilter} setRangoFilter={setRangoFilter}
              countsPorRango={countsPorRango}
              ccaaOptions={ccaaOptions} provinciaOptions={provinciaOptions}
              ccaasFilter={ccaasFilter} setCcaasFilter={setCcaasFilter}
              provinciasFilter={provinciasFilter} setProvinciasFilter={setProvinciasFilter}
              tamano={tamano} setTamano={setTamano}
              zonas={zonas} zonaFilter={zonaFilter} setZonaFilter={setZonaFilter}
            />
          </div>
        )}
      </Card>

      <DashboardMercado
        apiData={apiData}
        filteredGeo={filteredGeo}
        gruposVisibles={gruposVisibles}
        pobPorGrupo={pobPorGrupo}
        modo={modo}
        totalEspana={muniStats.total}
      />

      {showBottomSheet && entityResumen && (
        <MapaMobileBottomSheet
          resumen={entityResumen}
          onClose={() => { setSelectedEntity(null); setEntityResumen(null); }}
        />
      )}
    </div>
  );
}
